import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  createDivision,
  updateDivision,
  fetchLeague,
  fetchDivisionsForSeason,
  setCurrentDivision,
  clearLeagueSuccess,
} from '../../store/slices/leagueSlice';
import DivisionForm from '../../components/league/DivisionForm';
import type { CreateDivisionRequest, UpdateDivisionRequest } from '@sulis-liiga/common';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

const ManageDivisionPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId, seasonId, divisionId } = useParams<{
    leagueId: string;
    seasonId: string;
    divisionId: string;
  }>();
  const {
    currentLeague,
    currentDivision,
    divisions,
    loading,
    error,
    createSuccess,
    updateSuccess,
  } = useSelector((state: RootState) => state.league);

  const isEdit = !!divisionId && divisionId !== 'create';
  // Only navigate away on a success that this page actually triggered. A stale
  // createSuccess/updateSuccess flag (left over because the 2s timer was cancelled
  // when the user left a prior edit page) must not auto-navigate this page.
  const hasSubmittedRef = useRef(false);

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  // Clear leftover success flags when leaving so a cancelled success timer can't
  // leak a stale flag into the next edit page.
  useEffect(() => {
    return () => {
      dispatch(clearLeagueSuccess());
    };
  }, [dispatch]);

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      // Load this season's divisions so the edit form can resolve the division
      // from the URL id regardless of how the user arrived here (deep-link,
      // refresh, or a prior page that never loaded divisions).
      if (seasonId && seasonId !== 'create') {
        dispatch(fetchDivisionsForSeason({ leagueId, seasonId }));
      }
    }
  }, [dispatch, leagueId, seasonId, tenantResolved]);

  useEffect(() => {
    if (isEdit && divisionId) {
      const division = divisions.find(d => d.id === divisionId);
      if (division) {
        dispatch(setCurrentDivision(division));
      }
    } else {
      dispatch(setCurrentDivision(null));
    }
  }, [dispatch, divisionId, divisions, isEdit]);

  useEffect(() => {
    if ((createSuccess || updateSuccess) && hasSubmittedRef.current) {
      const timer = setTimeout(() => {
        dispatch(clearLeagueSuccess());
        navigate(`/leagues/${leagueId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [createSuccess, updateSuccess, navigate, dispatch, leagueId]);

  const handleDivisionSubmit = (data: CreateDivisionRequest | UpdateDivisionRequest) => {
    if (!leagueId || !seasonId) return;
    hasSubmittedRef.current = true;

    if (isEdit && divisionId) {
      dispatch(
        updateDivision({
          leagueId,
          seasonId,
          divisionId,
          divisionData: data as UpdateDivisionRequest,
        }),
      );
    } else {
      dispatch(createDivision({ leagueId, seasonId, divisionData: data as CreateDivisionRequest }));
    }
  };

  const handleCancel = () => {
    navigate(`/leagues/${leagueId}`);
  };

  // In edit mode the form edits the whole ladder, so build its initial data from
  // ALL tiers sharing the clicked division's group (settings + status are shared;
  // max teams is per tier). Recover the promotion/relegation config from an
  // interior tier (the top/bottom tiers store 0).
  const editInitialData = useMemo(() => {
    if (!isEdit || !currentDivision) return undefined;
    const groupId = currentDivision.divisionGroupId;
    const tiers = divisions
      .filter(d => (groupId ? d.divisionGroupId === groupId : d.id === currentDivision.id))
      .sort((a, b) => a.level - b.level);
    if (tiers.length === 0) return undefined;
    return {
      name: currentDivision.baseName ?? currentDivision.name,
      category: currentDivision.category,
      ageRestriction: currentDivision.ageRestriction,
      genderRestriction: currentDivision.genderRestriction,
      teamsToPromote: Math.max(0, ...tiers.map(t => t.teamsToPromote)),
      teamsToRelegate: Math.max(0, ...tiers.map(t => t.teamsToRelegate)),
      pointSystem: currentDivision.pointSystem,
      status: currentDivision.status,
      tiers: tiers.map(t => ({ maxTeams: t.maxTeams })),
    } as Partial<CreateDivisionRequest>;
  }, [isEdit, currentDivision, divisions]);

  if (loading && !currentLeague) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (!currentLeague) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={`/leagues/${leagueId}`}
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <svg
              className="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to League
          </Link>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {isEdit ? 'Manage Division' : 'Create New Division'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{currentLeague.name}</p>
        </div>

        {(createSuccess || updateSuccess) && (
          <div className="mb-6 rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Division {isEdit ? 'updated' : 'created'} successfully
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md bg-danger-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-danger-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <DivisionForm
              // Remount when the loaded division/ladder arrives so the form
              // re-initialises with its data (the form reads initialData once).
              key={currentDivision?.id ?? 'new'}
              initialData={isEdit ? editInitialData : undefined}
              onSubmit={handleDivisionSubmit}
              onCancel={handleCancel}
              loading={loading}
              isEdit={isEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageDivisionPage;
